import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import ConfirmationPopup from "../deletePopup/deletePopup";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MODULE_NAMES, STAFF_BELONGS_TO } from "../../config/enum";
import { SAMContext } from "../context/samContext";
import { useState, useEffect, useContext } from "react";
import {
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faPlus,
  faUserLock,
  faEye,
  faEyeSlash,
  faSearch,
  faUsers,
  faSlidersH,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const STATUS_CLS = {
  Active: "bg-green-100 text-green-700",
  Inactive: "bg-red-100 text-red-600",
};

const StaffAccessManagamentListing = () => {
  const {
    samSearchTerm: globalSearchTerm,
    setSamSearchTerm: setGlobalSearchTerm,
    samFilters: filters,
    setSamFilters: setFilters,
    ContextCurrentPage: currentPage,
    contextSetCurrentPage: setCurrentPage,
    ContextTotalPages: totalPages,
    contextSetTotalPages: setTotalPages,
    Contextlimit: limit,
    ContextReload: reload,
    ContextSetReload: setReload,
  } = useContext(SAMContext);

  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteUserData, setDeleteUserData] = useState({});
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [updateStaffData, setUpdateStaffData] = useState(null);
  const [showUpdatePWD, setShowUpdatePWD] = useState(false);
  const [updatePWDData, setUpdatePwdData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const getAllSAMAccounts = async () => {
      setLoading(true);
      const params = { page: currentPage, limit };
      try {
        let apiResponse;
        if (globalSearchTerm.trim()) {
          params.query = globalSearchTerm.trim();
          apiResponse = await func.get(urls.SEARCH_STAFF_URL, { params });
        } else if (Object.keys(filters).some((key) => filters[key])) {
          const modifiedFilters = { ...filters, accessModules: filters.accessModule?.value, staffBelongsTo: filters.staffBelongsTo?.value };
          apiResponse = await func.post(urls.FILTER_STAFF_URL, modifiedFilters, { params });
        } else {
          apiResponse = await func.get(urls.GET_ALL_STAFF_PAGINATED_URL, { params });
        }
        if (apiResponse?.status === 200) {
          setFilteredAccounts(apiResponse?.data?.staff || apiResponse?.data?.data || []);
          setTotalPages(apiResponse.data.totalPages);
        } else {
          toast.error("Error fetching staff accounts");
        }
      } catch {
        toast.error("Network error");
      }
      setLoading(false);
    };
    getAllSAMAccounts();
  }, [reload, currentPage]);

  const handleSearch = () => { setReload(!reload); setCurrentPage(1); };

  const applyFilters = () => { setReload(!reload); setShowFilter(false); setCurrentPage(1); setGlobalSearchTerm(""); };

  const resetFilters = () => {
    setFilters({ staffType: "", status: "", accessModule: "", staffBelongsTo: "", updatedAt: "", createdAt: "", startDate: "", endDate: "" });
    setCurrentPage(1);
    setGlobalSearchTerm("");
    setReload(!reload);
  };

  const handleAccept = async () => {
    setShowDeleteButton(false);
    if (filteredAccounts?.length === 1) setCurrentPage(Math.max(1, currentPage - 1));
    const res = await func.del(urls.DELETE_STAFF_URL + deleteUserData._id);
    setShowDeleteButton(true);
    if (res?.status === 200) {
      toast.success("Staff Account Deleted Successfully");
      setDeleteUserData({});
      setReload(!reload);
      setShowDeletePopup(false);
    } else {
      toast.warn("Error deleting staff account");
    }
  };

  const handleUpdatePWD = async () => {
    const newPassword = updatePWDData || "";
    if (newPassword.length < 8) { setNewPasswordError("Min 8 chars"); return; }
    if (!/[A-Z]/.test(newPassword)) { setNewPasswordError("Must have 1 uppercase letter"); return; }
    if (!/[a-z]/.test(newPassword)) { setNewPasswordError("Must have 1 lowercase letter"); return; }
    if (!/\d/.test(newPassword)) { setNewPasswordError("Must have 1 digit"); return; }
    if (!/[,]/.test(newPassword)) { setNewPasswordError("Must have 1 comma"); return; }
    if (!/[\W_]/.test(newPassword)) { setNewPasswordError("Must have 1 special character"); return; }
    if (newPassword.match(/([a-zA-Z0-9])\1{5}/)) { setNewPasswordError("No more than 5 same characters"); return; }
    if (newPassword.startsWith(",") || newPassword.endsWith(",")) { setNewPasswordError("Comma must not be at the start or end"); return; }
    try {
      const res = await func.put(urls.UPDATE_STAFF_PASSWORD_URL + updateStaffData._id, { password: newPassword });
      if (res?.status === 200) {
        toast.success("Password Updated Successfully");
        setShowUpdatePWD(false);
        setUpdatePwdData(null);
      } else {
        toast.error(res);
      }
    } catch {
      toast.error("An unexpected error occurred. Please try again.");
    }
    setNewPasswordError(null);
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faUsers} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Staff Access Management</h1>
              <p className="text-white/70 text-sm">Manage staff accounts and permissions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              Filters
            </button>
            <button
              onClick={() => navigate("/staff-access-management/add")}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Staff
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={globalSearchTerm}
              onChange={(e) => { setGlobalSearchTerm(e.target.value); if (!e.target.value.trim()) handleSearch(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={60}
              placeholder="Search by any field..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Name / Email", "Staff Type", "Created / Updated", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : filteredAccounts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faUsers} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No staff accounts found
                    </td>
                  </tr>
                ) : (
                  filteredAccounts.map((account, index) => (
                    <tr key={account._id || index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + index + 1}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{account.name}</p>
                        <p className="text-xs text-gray-400">{account.emailId}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700">{account.staffType}</p>
                        <p className="text-xs text-gray-400">{account?.staffBelongsTo || ""}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        <p>{new Date(account.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        <p className="text-gray-400">{new Date(account.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLS[account.status] || "bg-gray-100 text-gray-500"}`}>
                          {account.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => { setUpdateStaffData(account); setShowUpdatePWD(true); }}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md font-medium transition"
                            title="Update Password"
                          >
                            <FontAwesomeIcon icon={faUserLock} />
                          </button>
                          <button
                            onClick={() => navigate(`/staff-access-management/view/${account?._id}`)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/staff-access-management/edit/${account?._id}`)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}
      </div>

      {/* Delete Popup */}
      <ConfirmationPopup
        show={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onCancel={() => setShowDeletePopup(false)}
        onAccept={handleAccept}
        showDeleteButton={showDeleteButton}
        question={<>Are you sure you want to delete <span className="font-bold italic">{deleteUserData?.name}</span>?</>}
        title="Delete Confirmation"
        acceptText="Yes, Delete"
        cancelText="Cancel"
      />

      {/* Filter Modal */}
      {showFilter && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowFilter(false)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800">Filter Staff</h3>
              <button onClick={() => setShowFilter(false)} className="text-gray-400 hover:text-gray-600 transition">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Staff Type</label>
                <select value={filters.staffType} onChange={(e) => setFilters({ ...filters, staffType: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white">
                  <option value="">All</option>
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
              {filters.staffType === "staff" && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Staff Belongs To</label>
                  <Select value={filters.staffBelongsTo} onChange={(e) => setFilters({ ...filters, staffBelongsTo: e })} options={STAFF_BELONGS_TO.map((e) => ({ label: e, value: e }))} isClearable />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Account Status</label>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 bg-white">
                  <option value="">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Access Modules</label>
                <Select value={filters.accessModule} onChange={(e) => setFilters({ ...filters, accessModule: e })} options={MODULE_NAMES.map((e) => ({ label: e, value: e }))} isClearable />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                <input type="date" value={filters.startDate || ""} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                <input type="date" value={filters.endDate || ""} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Updated At</label>
                <input type="date" value={filters.updatedAt} onChange={(e) => setFilters({ ...filters, updatedAt: e.target.value })} className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button onClick={resetFilters} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Reset</button>
              <button onClick={applyFilters} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Update Password Modal */}
      {showUpdatePWD && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Update Password</h3>
              <button
                onClick={() => { setShowUpdatePWD(false); setUpdateStaffData(null); setUpdatePwdData(null); setNewPasswordError(null); }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={updatePWDData || ""}
                  onChange={(e) => { setUpdatePwdData(e.target.value); setNewPasswordError(null); }}
                  onInput={(e) => { e.target.value = e.target.value.slice(0, 20); }}
                  placeholder="Enter new password"
                  className="w-full text-sm px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {newPasswordError && <p className="text-red-600 text-xs mt-1">{newPasswordError}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => { setShowUpdatePWD(false); setUpdateStaffData(null); setUpdatePwdData(null); setNewPasswordError(null); }}
                className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button onClick={handleUpdatePWD} className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium">
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default StaffAccessManagamentListing;
